import { useState, useEffect } from 'react'
import { Sparkles, X, ArrowRight } from 'lucide-react'

/**
 * Popup de divulgação de release.
 * - Aparece para todo usuário logado até a data de expiração.
 * - Cada nova sessão (nova aba/login) que ainda não atingiu o limite reabre o popup.
 * - Ao dispensar (X, "Ver depois" ou clique no CTA) pela 3ª vez, nunca mais reaparece.
 * - Dentro da mesma sessão, não reaparece após o primeiro dismiss.
 * - Para criar um novo popup no futuro, atualizar STORAGE_KEY/SESSION_KEY (chaves
 *   novas → usuários que já dispensaram o anterior veem o novo).
 */
const STORAGE_KEY = 'release-popup-2026-05-13-dismiss-count'
const SESSION_KEY = 'release-popup-2026-05-13-session-shown'
const MAX_DISMISSALS = 3
// Exportados pra que o botão "Novas Features" no Header use o mesmo prazo/URL.
export const RELEASE_EXPIRES_AT = new Date('2026-06-13T23:59:59-03:00')
export const RELEASE_URL = 'https://social.myncc.com.br/portfolio/release-crm-2026-05-07.html'
const EXPIRES_AT = RELEASE_EXPIRES_AT

function getDismissCount(): number {
  const v = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10)
  return Number.isFinite(v) ? v : 0
}

export function ReleasePopup() {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (Date.now() > EXPIRES_AT.getTime()) return
    if (getDismissCount() >= MAX_DISMISSALS) return
    if (sessionStorage.getItem(SESSION_KEY) === '1') return
    // Pequeno delay pra não atrapalhar o primeiro paint
    const t = setTimeout(() => setOpen(true), 600)
    return () => clearTimeout(t)
  }, [])

  const dismiss = () => {
    const next = getDismissCount() + 1
    localStorage.setItem(STORAGE_KEY, String(next))
    sessionStorage.setItem(SESSION_KEY, '1')
    setOpen(false)
  }

  const openRelease = () => {
    window.open(RELEASE_URL, '_blank', 'noopener,noreferrer')
    dismiss()
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-in fade-in duration-200 p-4"
      onClick={dismiss}
    >
      <div
        className="relative w-full max-w-md rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {/* Background com gradiente NAI (dark blue → cyan) */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#005092] via-[#003a6e] to-[#00284a]" />
        {/* Brilho decorativo */}
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[#00d4aa]/30 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-[#3b9bd6]/20 blur-3xl" />

        {/* Botão de fechar */}
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors"
          aria-label="Fechar"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="relative p-8 text-center">
          {/* Ícone */}
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#00d4aa] text-[#0a0f1a] mb-5 shadow-lg shadow-[#00d4aa]/30">
            <Sparkles className="h-8 w-8" />
          </div>

          {/* Badge */}
          <div className="inline-block bg-[#00d4aa] text-[#0a0f1a] text-[11px] font-bold px-3 py-1 rounded-full uppercase tracking-widest mb-4">
            Novidade
          </div>

          {/* Título */}
          <h2 className="text-2xl font-extrabold text-white mb-2 leading-tight">
            NOVAS FEATURES
          </h2>
          <p className="text-white/80 text-sm mb-6">
            Dashboards estratégicos de previsibilidade + IA que sugere a próxima melhor ação em cada oportunidade. 35+ entregas para acelerar sua rotina comercial.
          </p>

          {/* CTA principal */}
          <button
            onClick={openRelease}
            className="group w-full bg-[#00d4aa] hover:bg-[#00e8ba] text-[#0a0f1a] font-bold text-sm uppercase tracking-wider py-3.5 px-6 rounded-xl transition-all hover:shadow-xl hover:shadow-[#00d4aa]/40 flex items-center justify-center gap-2"
          >
            CLIQUE AQUI E SAIBA MAIS
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </button>

          {/* Link discreto pra fechar */}
          <button
            onClick={dismiss}
            className="mt-3 text-white/60 hover:text-white/90 text-xs underline-offset-2 hover:underline transition-colors"
          >
            Ver depois
          </button>
        </div>
      </div>
    </div>
  )
}
